/* Chef Akila – basket (section "Basket", sections/ca-cart-drawer.liquid).
   One API for every add-to-basket on the site:
     CACart.add(items, { source })  → POST /cart/add.js, refresh the basket + bag count, show the "Added" feedback
     CACart.refresh()               → re-render the basket after a change made elsewhere
     CACart.open() / CACart.close()
   Also: delivery day picker (shared CADelivery engine, cart attributes Shipping-Date / preferred_day / Delivery Date),
   Saturday charge line, £39 minimum lock, courier note, focus trap, and backwards-compatible shims for
   window.openCartDrawer() and document.querySelector('cart-slide').refreshCart(). */
(() => {
  if (window.CACart && window.CACart.__ready) return;

  const OWN = { 'X-CA-Cart': '1' };
  const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json', ...OWN };
  const routes = window.routes || {};
  const CART_URL = routes.cart_url || '/cart';
  const ROOT_URL = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  let root, cfg, T, sectionId, mode;
  let data = null; // what the section last rendered (counts, totals, lines)
  let lastCart = null; // last /cart.js-shaped cart
  let selectedDate = ''; // YYYY-MM-DD
  let timeReady = false; // server clock known
  let lastFocus = null;

  const $ = (sel, el = root) => (el ? el.querySelector(sel) : null);
  const $$ = (sel, el = root) => (el ? Array.from(el.querySelectorAll(sel)) : []);
  const esc = (t) => String(t ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const D = () => window.CADelivery;

  const money = (cents) => {
    const fmt = (cfg && cfg.moneyFormat) || '£{{amount}}';
    const value = (Number(cents || 0) / 100).toFixed(2);
    const [whole, dec] = value.split('.');
    const w = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, w)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${whole},${dec}`)
      .replace(/\{\{\s*amount\s*\}\}/, `${w}.${dec}`);
  };
  const fill = (str, map) => String(str || '').replace(/\[(\w+)\]/g, (m, k) => (k in map ? map[k] : m));

  /* ---------- network ---------- */
  const OFFLINE = 'We couldn’t reach the basket. Please check your connection and try again.';
  const post = async (url, body) => {
    let res;
    try {
      res = await fetch(url, { method: 'POST', credentials: 'same-origin', headers: JSON_HEADERS, body: JSON.stringify(body) });
    } catch (e) {
      const err = new Error(OFFLINE);
      err.status = 0;
      throw err;
    }
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(json.description || json.message || (T && T.error) || 'Sorry, something went wrong. Please try again.');
      err.status = res.status;
      throw err;
    }
    return json;
  };
  const getCart = () =>
    fetch('/cart.js', { cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'application/json', ...OWN } })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
  const getSection = () => {
    const base = mode === 'page' ? CART_URL : ROOT_URL;
    return fetch(`${base}${base.includes('?') ? '&' : '?'}sections=${encodeURIComponent(sectionId)}&_=${Date.now()}`, { credentials: 'same-origin', headers: OWN })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => (j && j[sectionId]) || null)
      .catch(() => null);
  };

  /* ---------- render ---------- */
  const readData = () => {
    try { data = JSON.parse($('[data-ca-cart-data]')?.textContent || 'null'); } catch (e) { data = null; }
    return data;
  };

  const apply = (html) => {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const active = document.activeElement;
    const noteFocused = active && active.matches && active.matches('[data-ca-cart-note]') && root.contains(active);
    const noteVal = noteFocused ? active.value : null;
    const sel = noteFocused ? [active.selectionStart, active.selectionEnd] : null;
    const focusKey = active && root.contains(active) ? focusSignature(active) : null;
    $$('[data-ca-region]').forEach((region) => {
      const fresh = doc.querySelector(`[data-ca-region="${region.dataset.caRegion}"]`);
      if (fresh) region.innerHTML = fresh.innerHTML;
    });
    readData();
    if (noteFocused) {
      const input = $('[data-ca-cart-note]');
      if (input) {
        input.value = noteVal;
        input.focus({ preventScroll: true });
        try { input.setSelectionRange(sel[0], sel[1]); } catch (e) { /* ignore */ }
        updateNoteCount(input);
      }
    } else if (focusKey) {
      restoreFocus(focusKey);
    }
    afterRender();
  };

  // keep keyboard users where they were after a re-render
  const focusSignature = (el) => {
    const line = el.closest('[data-ca-line]');
    const attrs = ['data-ca-cart-qty', 'data-ca-cart-remove', 'data-ca-cart-add', 'data-ca-sheet-open', 'data-ca-cart-clear', 'data-ca-cart-checkout'];
    const a = attrs.find((x) => el.hasAttribute(x));
    if (!a) return null;
    return { key: line?.dataset.key, attr: a, val: el.getAttribute(a) };
  };
  const restoreFocus = (sig) => {
    let el = null;
    if (sig.key) {
      const line = $(`[data-ca-line][data-key="${CSS.escape(sig.key)}"]`);
      el = line && (line.querySelector(`[${sig.attr}="${CSS.escape(sig.val || '')}"]`) || line.querySelector(`[${sig.attr}]`));
    } else {
      el = $(`[${sig.attr}="${CSS.escape(sig.val || '')}"]`) || $(`[${sig.attr}]`);
    }
    if (!el) el = $('[data-ca-cart-panel]');
    el?.focus({ preventScroll: true });
  };

  // phones: the green "Add £x and save £6.95" button can be hidden with its ×, for this visit
  let saveHidden = false;
  try { saveHidden = sessionStorage.getItem('ca-cart-save-hidden') === '1'; } catch (e) { /* private mode */ }
  const paintSave = () => { $('[data-ca-cart-save]')?.classList.toggle('is-hidden', saveHidden); };

  const afterRender = () => {
    paintSave();
    if (typeof paintQty === 'function') paintQty();
    renderDay();
    updateBubble();
    const n = $('[data-ca-cart-note]');
    if (n) updateNoteCount(n);
    renderBar('settled', null, true);
  };

  const updateBubble = () => {
    if (!data) return;
    const count = data.count;
    document.querySelectorAll('.cart-count-bubble').forEach((b) => {
      const span = b.querySelector('span[aria-hidden]') || b.querySelector('span') || b;
      span.textContent = count > 0 ? String(count) : '';
    });
    document.querySelectorAll('.ca-header__bag').forEach((a) => a.setAttribute('aria-label', count > 0 ? `Basket, ${count} ${count === 1 ? 'item' : 'items'}` : 'Basket'));
  };

  const emit = (cart) => {
    if (!cart) return;
    try { Object.defineProperty(cart, '__ca', { value: true }); } catch (e) { /* frozen */ }
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart, bubbles: true }));
    updateBubble(); // after other listeners (they count the Saturday charge as an item)
  };

  let seq = 0;
  const refresh = async (opts = {}) => {
    const mine = ++seq;
    const panel = $('[data-ca-cart-panel]');
    panel?.classList.add('is-busy');
    const [html, cart] = await Promise.all([getSection(), opts.cart ? Promise.resolve(opts.cart) : getCart()]);
    if (mine !== seq) return cart; // a newer refresh is on its way
    panel?.classList.remove('is-busy');
    if (cart) lastCart = cart;
    apply(html);
    emit(cart);
    if (!opts.skipReconcile) await reconcile(cart);
    return cart;
  };

  // another tab, the back button (page cache) or an app may have changed the basket since we last drew it
  const sig = (c) => (c ? `${(c.items || []).map((i) => `${i.key}:${i.quantity}`).join(',')}|${c.note || ''}|${(c.attributes || {})['Shipping-Date'] || ''}` : '');
  let syncBusy = false;
  const syncIfStale = async () => {
    if (!root || syncBusy) return;
    syncBusy = true;
    try {
      const cart = await getCart();
      if (!cart) return;
      const drawn = data && (cart.item_count !== data.itemCount || cart.total_price !== data.total);
      if (drawn || sig(cart) !== sig(lastCart)) {
        const noteEl = $('[data-ca-cart-note]');
        if (noteEl && document.activeElement !== noteEl) noteEl.value = cart.note || '';
        await refresh({ cart });
      }
    } finally {
      syncBusy = false;
    }
  };

  /* ---------- delivery date + Saturday charge kept in step with the basket ---------- */
  const attrsFor = (iso) => ({
    'Shipping-Date': iso,
    preferred_day: D().format(iso, 'weekday'),
    'Delivery Date': D().format(iso, 'dmy'),
  });

  const satLines = (cart) => (cart?.items || []).filter((i) => i.variant_id === Number(cfg.satVariant));
  const mealsCount = (cart) => (cart ? cart.item_count - satLines(cart).reduce((n, i) => n + i.quantity, 0) : 0);
  // eGift vouchers (gift cards, nothing to ship) on their own: no minimum, no delivery charge, no delivery day
  const isVoucher = (i) => !!(i.gift_card || i.requires_shipping === false);
  const voucherOnly = (cart) => {
    const lines = (cart?.items || []).filter((i) => i.variant_id !== Number(cfg.satVariant));
    return lines.length > 0 && lines.every(isVoucher);
  };
  const satWanted = (cart, iso) => (mealsCount(cart) > 0 && !voucherOnly(cart) && cfg.satFeeOn && cfg.satVariant && iso && D().isSaturday(D().parse(iso)) ? 1 : 0);

  let reconciling = false;
  const reconcile = async (cart) => {
    if (!cart || reconciling || !timeReady || !D()) return;
    reconciling = true;
    try {
      const attrs = cart.attributes || {};
      let iso = attrs['Shipping-Date'] || '';
      const meals = mealsCount(cart);
      const body = {};
      if (meals > 0 && !voucherOnly(cart)) {
        if (!D().isValidChoice(iso)) iso = D().iso(D().earliest());
        const want = attrsFor(iso);
        if (Object.keys(want).some((k) => attrs[k] !== want[k])) body.attributes = want;
      }
      if (iso) selectedDate = iso;
      const satQty = satLines(cart).reduce((n, i) => n + i.quantity, 0);
      const wantSat = satWanted(cart, iso);
      let itemsChanged = false;
      if (satQty !== wantSat) {
        if (satLines(cart).length > 1 || (wantSat === 0 && satQty > 0)) {
          body.updates = Object.fromEntries(satLines(cart).map((l) => [l.key, 0]));
          if (wantSat) body.updates[satLines(cart)[0].key] = 1;
        } else {
          body.updates = { [cfg.satVariant]: wantSat };
        }
        itemsChanged = true;
      }
      if (!body.attributes && !body.updates) { renderDay(); return; }
      let fresh;
      try {
        fresh = await post('/cart/update.js', body);
      } catch (err) {
        // e.g. the Saturday charge product is unavailable: keep the date, say so
        if (body.updates && body.attributes) fresh = await post('/cart/update.js', { attributes: body.attributes }).catch(() => null);
        footMessage(err.message);
      }
      if (fresh) lastCart = fresh;
      renderDay();
      if (itemsChanged && fresh) {
        reconciling = false;
        await refresh({ cart: fresh, skipReconcile: true });
      }
    } finally {
      reconciling = false;
    }
  };

  const setDate = async (iso) => {
    if (!D().isValidChoice(iso)) return;
    selectedDate = iso;
    renderDay();
    const cart = lastCart || (await getCart());
    const body = { attributes: attrsFor(iso) };
    const satQty = satLines(cart).reduce((n, i) => n + i.quantity, 0);
    const wantSat = satWanted(cart, iso);
    if (satQty !== wantSat) {
      body.updates = satLines(cart).length ? Object.fromEntries(satLines(cart).map((l, i) => [l.key, i === 0 ? wantSat : 0])) : { [cfg.satVariant]: wantSat };
    }
    try {
      const fresh = await post('/cart/update.js', body);
      lastCart = fresh;
      if (body.updates) await refresh({ cart: fresh, skipReconcile: true });
      else renderDay();
    } catch (err) {
      footMessage(err.message);
      await refresh();
    }
  };

  const renderDay = () => {
    const card = $('[data-ca-cart-day]');
    if (!card || !D()) return;
    if (!selectedDate || !D().parse(selectedDate)) selectedDate = (lastCart?.attributes || {})['Shipping-Date'] || data?.date || '';
    let iso = selectedDate;
    if (timeReady && !D().isValidChoice(iso)) iso = D().iso(D().earliest());
    if (!iso) iso = D().iso(D().earliest());
    const d = D().parse(iso);
    $('[data-ca-day-date]', card).textContent = D().format(d, 'long');
    const help = $('[data-ca-day-help]', card);
    if (help && !help.classList.contains('is-alert')) {
      let text = '';
      if (D().isEarliest(d)) text = T.dayHelper;
      else if (D().hasSaturdayFee(d)) text = fill(T.daySaturday, { fee: `+${money(cfg.satPrice)}` });
      help.textContent = text;
      help.hidden = !text;
    }
  };

  const footMessage = (msg) => {
    const el = $('[data-ca-cart-foot-msg]');
    if (!el) return;
    el.textContent = msg || '';
    el.hidden = !msg;
  };

  /* ---------- delivery day sheet ---------- */
  let pending = '';
  let calMonth = null;
  const sheet = () => $('[data-ca-cart-sheet]');
  const sheetOpen = () => sheet()?.classList.contains('is-open');

  const optionHTML = (d, i) => {
    const iso = D().iso(d);
    const on = iso === pending;
    let sub = '';
    if (i === 0 && D().isNextDay(d)) sub = T.nextDay;
    if (D().hasSaturdayFee(d)) sub = T.saturday;
    const badge = D().isEarliest(d) ? `<span class="ca-cart-opt__badge">${esc(T.earliest)}</span>` : '';
    const fee = D().hasSaturdayFee(d) ? `<span class="ca-cart-opt__fee">+${esc(money(cfg.satPrice))}</span>` : '';
    return `<label class="ca-cart-opt${on ? ' is-on' : ''}">
      <input type="radio" name="ca-cart-day-${esc(sectionId)}" value="${iso}"${on ? ' checked' : ''}>
      <span class="ca-cart-opt__radio" aria-hidden="true"></span>
      <span class="ca-cart-opt__text"><span class="ca-cart-opt__day">${esc(D().format(d, 'long'))}</span>${sub ? `<span class="ca-cart-opt__sub">${esc(sub)}</span>` : ''}</span>
      ${badge}${fee}
    </label>`;
  };

  const renderSheet = () => {
    const s = sheet();
    if (!s) return;
    $('[data-ca-sheet-intro]', s).textContent = fill(T.sheetIntro, { cutoff: D().cutoffLabel(), days: D().daysLabel() });
    const list = D().upcoming(5);
    const extra = D().parse(pending);
    if (extra && !list.some((d) => D().iso(d) === pending)) list.push(extra);
    $('[data-ca-sheet-list]', s).innerHTML = list.map(optionHTML).join('');
    $('[data-ca-sheet-confirm]', s).textContent = fill(T.confirm, { date: D().format(pending, 'long') });
    const cal = $('[data-ca-sheet-cal]', s);
    if (!cal.hidden) renderCal();
  };

  const renderCal = () => {
    const cal = $('[data-ca-sheet-cal]', sheet());
    const first = D().earliest();
    const last = D().lastDay();
    if (!calMonth) {
      const p = D().parse(pending) || first;
      calMonth = new Date(Date.UTC(p.getUTCFullYear(), p.getUTCMonth(), 1));
    }
    const minMonth = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), 1));
    const maxMonth = new Date(Date.UTC(last.getUTCFullYear(), last.getUTCMonth(), 1));
    const y = calMonth.getUTCFullYear();
    const m = calMonth.getUTCMonth();
    const lead = (new Date(Date.UTC(y, m, 1)).getUTCDay() + 6) % 7; // Monday first
    const daysIn = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
    let cells = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((w) => `<span class="ca-cart-cal__wd" aria-hidden="true">${w}</span>`).join('');
    for (let i = 0; i < lead; i++) cells += '<span></span>';
    for (let day = 1; day <= daysIn; day++) {
      const d = new Date(Date.UTC(y, m, day));
      const iso = D().iso(d);
      const ok = d >= first && d <= last && D().isDeliveryDay(d);
      const on = iso === pending;
      const sat = D().hasSaturdayFee(d);
      const label = `${D().format(d, 'long')}${sat ? `, ${T.saturday} +${money(cfg.satPrice)}` : ''}${ok ? '' : ', not available'}`;
      cells += `<button type="button" class="ca-cart-cal__day${on ? ' is-on' : ''}${sat ? ' is-sat' : ''}" data-ca-cal-day="${iso}" aria-label="${esc(label)}" aria-pressed="${on}"${ok ? '' : ' disabled'}>${day}</button>`;
    }
    const icons = iconTpl();
    cal.innerHTML = `<div class="ca-cart-cal__head">
        <button type="button" class="ca-cart-cal__nav" data-ca-cal-nav="-1" aria-label="Previous month"${calMonth <= minMonth ? ' disabled' : ''}>${icons['chev-l']}</button>
        <span class="ca-cart-cal__month" aria-live="polite">${esc(D().format(calMonth, 'month'))}</span>
        <button type="button" class="ca-cart-cal__nav" data-ca-cal-nav="1" aria-label="Next month"${calMonth >= maxMonth ? ' disabled' : ''}>${icons['chev-r']}</button>
      </div><div class="ca-cart-cal__grid">${cells}</div>`;
  };

  const openSheet = async (opener) => {
    const s = sheet();
    if (!s) return;
    if (!timeReady) { await D().syncServerTime(); timeReady = true; }
    pending = D().isValidChoice(selectedDate) ? selectedDate : D().iso(D().earliest());
    calMonth = null;
    $('[data-ca-sheet-cal]', s).hidden = true;
    $('[data-ca-sheet-list]', s).hidden = false;
    $('[data-ca-sheet-more]', s).hidden = false;
    renderSheet();
    s.classList.add('is-open');
    s.setAttribute('aria-hidden', 'false');
    s._opener = opener;
    if (mode === 'page') lockScroll(true);
    requestAnimationFrame(() => (s.querySelector('input:checked') || $('.ca-cart-sheet__panel', s)).focus({ preventScroll: true }));
  };
  const closeSheet = () => {
    const s = sheet();
    if (!s || !sheetOpen()) return;
    s.classList.remove('is-open');
    s.setAttribute('aria-hidden', 'true');
    if (mode === 'page') lockScroll(false);
    const back = s._opener && document.contains(s._opener) ? s._opener : $('[data-ca-sheet-open]');
    back?.focus({ preventScroll: true });
  };

  /* ---------- drawer ---------- */
  const drawer = () => $('[data-ca-cart-drawer]');
  const isOpen = () => !!drawer()?.classList.contains('is-open');
  let scrollLocks = 0;
  const lockScroll = (on) => {
    scrollLocks = Math.max(0, scrollLocks + (on ? 1 : -1));
    const lock = scrollLocks > 0;
    document.documentElement.style.overflow = lock ? 'hidden' : '';
    document.documentElement.classList.toggle('ca-cart-locked', lock);
    document.body.style.overflow = lock ? 'hidden' : '';
  };

  const open = (opener) => {
    if (!root) { window.location.href = CART_URL; return; }
    if (mode === 'page') { $('[data-ca-cart-panel]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
    const d = drawer();
    if (!d || isOpen()) return;
    lastFocus = opener instanceof Element ? opener : document.activeElement;
    hidePop();
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    document.querySelectorAll('.ca-header__bag').forEach((b) => b.setAttribute('aria-expanded', 'true'));
    lockScroll(true);
    const scroller = $('[data-ca-cart-scroll]');
    if (scroller) scroller.scrollTop = 0;
    requestAnimationFrame(() => $('[data-ca-cart-panel]')?.focus({ preventScroll: true }));
    if (timeReady) renderDay();
    syncIfStale();
  };
  const close = () => {
    const d = drawer();
    if (!d || !isOpen()) return;
    closeSheet();
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    document.querySelectorAll('.ca-header__bag').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    lockScroll(false);
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
    renderBar('settled', null, true);
  };

  const focusables = (scope) =>
    Array.from(scope.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), [tabindex]:not([tabindex="-1"])')).filter(
      (el) => el.offsetParent !== null || el.getClientRects().length
    );

  /* ---------- note ---------- */
  let noteTimer;
  const updateNoteCount = (input) => {
    const c = $('[data-ca-note-count]');
    if (c) c.textContent = `${input.value.length}/${cfg.noteMax} characters`;
  };
  const saveNote = async (input) => {
    clearTimeout(noteTimer);
    const note = input.value.slice(0, cfg.noteMax);
    if (lastCart && lastCart.note === note) return;
    try {
      const fresh = await post('/cart/update.js', { note });
      lastCart = fresh;
    } catch (err) { footMessage(err.message); }
  };

  /* ---------- line actions ---------- */
  const lineError = (key, msg) => {
    const line = $(`[data-ca-line][data-key="${CSS.escape(key)}"]`);
    const el = line?.querySelector('[data-ca-line-error]');
    if (!el) return;
    el.textContent = msg;
    el.hidden = !msg;
  };
  const changeLine = async (line, qty) => {
    const key = line.dataset.key;
    const n = line.querySelector('.ca-cart-qty__n');
    if (n && qty > 0) n.textContent = String(qty);
    line.setAttribute('aria-busy', 'true');
    try {
      const cart = await post('/cart/change.js', { id: key, quantity: qty });
      await refresh({ cart });
    } catch (err) {
      await refresh();
      lineError(key, err.message);
    }
  };

  // + / − taps: show the new number straight away, send one change after a short pause (fast taps add up)
  const wantQty = {};
  const qtyTimers = {};
  const qtyBusy = {};
  const paintQty = () => {
    Object.keys(wantQty).forEach((key) => {
      const n = $(`[data-ca-line][data-key="${CSS.escape(key)}"] .ca-cart-qty__n`);
      if (n && wantQty[key] > 0) n.textContent = String(wantQty[key]);
    });
  };
  const flushQty = async (key) => {
    if (qtyBusy[key]) { qtyTimers[key] = setTimeout(() => flushQty(key), 150); return; }
    if (!(key in wantQty)) return;
    const target = wantQty[key];
    delete wantQty[key];
    const line = $(`[data-ca-line][data-key="${CSS.escape(key)}"]`);
    if (!line || target === Number(line.dataset.qty)) return;
    qtyBusy[key] = true;
    try { await changeLine(line, target); } finally { delete qtyBusy[key]; }
  };
  const queueQty = (line, delta) => {
    const key = line.dataset.key;
    const next = Math.max(0, (key in wantQty ? wantQty[key] : Number(line.dataset.qty)) + delta);
    wantQty[key] = next;
    paintQty();
    clearTimeout(qtyTimers[key]);
    qtyTimers[key] = setTimeout(() => flushQty(key), 300);
  };

  /* ---------- add (the one entry point for every add-to-basket button) ---------- */
  const add = async (items, opts = {}) => {
    const list = (Array.isArray(items) ? items : [items])
      .map((i) => ({ ...i, id: Number(i.id), quantity: Number(i.quantity || 1) }))
      .filter((i) => i.id && i.id !== Number(cfg?.satVariant));
    if (!list.length) throw new Error((T && T.error) || 'Sorry, something went wrong. Please try again.');
    const before = data ? { meals: data.meals, count: data.count } : null;
    const res = await post('/cart/add.js', { items: list });
    if (!root) {
      const cart = await getCart();
      emit(cart);
      return res;
    }
    await refresh();
    const added = (res.items || [res]).map((it, i) => ({ key: it.key, variant: it.variant_id, qty: list[i]?.quantity || 1, item: it }));
    if (opts.feedback !== false && !isOpen() && mode !== 'page') feedback(added, before);
    return res;
  };

  /* ---------- "Added to your basket" feedback ---------- */
  let iconsCache = null;
  const iconTpl = () => {
    if (iconsCache) return iconsCache;
    iconsCache = {};
    const t = $('template[data-ca-cart-icons]');
    if (t) t.content.querySelectorAll('[data-i]').forEach((s) => (iconsCache[s.dataset.i] = s.innerHTML));
    return iconsCache;
  };
  const progHTML = (st, meals) => {
    const I = iconTpl();
    const min = cfg.min;
    const free = cfg.free;
    const pct = meals <= min ? (meals * 55) / min : Math.min(100, 55 + ((meals - min) * 45) / (free - min));
    const minOn = st !== 'min';
    const freeOn = st === 'free';
    return `<div class="ca-cart-fb__prog${freeOn ? ' is-free' : ''}" aria-hidden="true">
      <span class="ca-cart-prog__track"><span class="ca-cart-prog__fill" style="width:${pct.toFixed(2)}%"></span></span>
      <span class="ca-cart-fb__pill ca-cart-fb__pill--min${minOn ? ' is-on' : ''}">${minOn ? I.check12 : I.lock12}<span>${esc(cfg.minLabel)}</span></span>
      <span class="ca-cart-fb__pill ca-cart-fb__pill--free${freeOn ? ' is-on' : ''}">${I.truck12}<span>${freeOn ? 'FREE' : esc(cfg.freeLabel)}</span></span>
    </div>`;
  };
  const strongAmt = (cents) => `<strong>${esc(money(cents))}</strong>`;
  const itemsLabel = (n) => fill(n === 1 ? T.item : T.items, { count: n });
  const variantLabel = (meta) => {
    const first = String(meta || '').split(' · ')[0];
    return first && !/^Serves/i.test(first) ? first : '';
  };

  // phone bar: phase 'added' (just added, ~4 s) or 'settled' (summary)
  let barTimer;
  let barPhase = 'off';
  let barInfo = null;
  let barBefore = null;
  const bar = () => $('[data-ca-cart-bar]');
  const isPhone = () => window.matchMedia('(max-width: 749px)').matches;
  const pdpSticky = () => document.querySelector('[data-ca-sticky].is-visible');

  const renderBar = (phase, info, passive) => {
    const b = bar();
    if (!b || !data) return;
    if (passive) {
      // re-draw what's showing (basket changed / page loaded); never start a new "added" phase
      if (barPhase === 'added') phase = 'added';
      else phase = cfg.barPersist ? 'settled' : 'off';
      info = barInfo;
    }
    if (data.count <= 0 || isOpen() || (phase === 'settled' && !cfg.barPersist)) phase = 'off';
    if (phase === 'settled' && pdpSticky()) phase = 'off'; // the product page's own sticky bar has the bottom of the screen
    barPhase = phase;
    if (phase === 'off') { b.classList.remove('is-shown'); b.setAttribute('aria-hidden', 'true'); layoutBar(); setTimeout(() => { if (barPhase === 'off') b.hidden = true; }, 300); return; }
    const I = iconTpl();
    const st = data.state;
    const before = barBefore;
    const crossedMin = !!before && before.meals < cfg.min && data.meals >= cfg.min;
    const crossedFree = !!before && before.meals < cfg.free && data.meals >= cfg.free;
    const toMin = cfg.min - data.meals;
    const toFree = cfg.free - data.meals;
    let status = '';
    if (st === 'voucher') {
      status = `<div class="ca-cart-fb__line">${I['circle-check']}<span class="ca-cart-fb__head">${esc(phase === 'added' ? T.added : T.voucher || T.added)}</span></div>${phase === 'added' && T.voucher ? `<div class="ca-cart-fb__sub">${esc(T.voucher)}</div>` : ''}`;
    } else if (phase === 'added' && !(crossedFree && st === 'free')) {
      let sub = '';
      let good = false;
      if (st === 'min') sub = fill(esc(T.fbMin), { amount: strongAmt(toMin), min: esc(cfg.minLabel) });
      else if (st === 'fee') { if (crossedMin) { sub = fill(esc(T.fbReached), { min: esc(cfg.minLabel) }); good = true; } else sub = fill(esc(T.fbFeeMore), { amount: strongAmt(toFree) }); }
      else { sub = esc(T.fbFree); good = true; }
      status = `<div class="ca-cart-fb__line">${I['circle-check']}<span class="ca-cart-fb__head">${esc(T.added)}</span></div><div class="ca-cart-fb__sub${good ? ' is-good' : ''}">${sub}</div>`;
    } else if (st === 'min') {
      status = `<div class="ca-cart-fb__line ca-cart-fb__line--lock">${I.lock}<span class="ca-cart-fb__lock">${fill(esc(T.fbMin), { amount: strongAmt(toMin), min: esc(cfg.minLabel) })}</span></div>`;
    } else if (st === 'fee') {
      status = `<div class="ca-cart-fb__line">${I['circle-check']}<span class="ca-cart-fb__head">${fill(esc(T.fbReached), { min: esc(cfg.minLabel) })}</span></div><div class="ca-cart-fb__sub">${fill(esc(T.fbFeeMore), { amount: strongAmt(toFree) })}</div>`;
    } else {
      status = `<div class="ca-cart-fb__line">${I['circle-check']}<span class="ca-cart-fb__head">${esc(T.fbFree)}</span></div><div class="ca-cart-fb__sub">${fill(esc(T.fbFreeSub), { min: esc(cfg.minLabel) })}</div>`;
    }
    const sum = `${itemsLabel(data.count)} · ${money(data.meals)}`;
    let row;
    if (phase === 'added' && info) {
      const label = variantLabel(info.meta);
      row = `<span class="ca-cart-fb__thumb">${info.image ? `<img src="${esc(info.image)}" alt="" width="44" height="44">` : ''}</span>
        <span class="ca-cart-fb__text"><span class="ca-cart-fb__title">${esc(info.title)}</span><span class="ca-cart-fb__meta">${esc(label ? `${label} · ${sum}` : sum)}</span></span>`;
    } else {
      row = `<span class="ca-cart-fb__bag">${I.bag}<span class="ca-cart-fb__count">${data.count}</span></span><span class="ca-cart-fb__text"><span class="ca-cart-fb__sum">${esc(sum)}</span></span>`;
    }
    b.innerHTML = `<div class="ca-cart-fb ca-cart-fb__status">${status}</div>${st === 'voucher' ? '' : progHTML(st, data.meals)}<div class="ca-cart-fb__row">${row}<button type="button" class="ca-cart-fb__view" data-ca-cart-open-bar aria-haspopup="dialog">${esc(T.view)}</button></div>`;
    b.hidden = false;
    b.setAttribute('aria-hidden', 'false');
    // (re)start the slide-in on the next frame so the transition runs
    requestAnimationFrame(() => { b.classList.add('is-shown'); layoutBar(); });
  };

  // while "Added" shows it takes the product page sticky bar's place (client 30OCT: one dish row, not the same dish twice),
  // and keeps the page's last content reachable
  const layoutBar = () => {
    const b = bar();
    if (!b) return;
    const sticky = pdpSticky();
    const shown = barPhase !== 'off' && isPhone();
    const html = document.documentElement;
    html.classList.toggle('ca-bar-over-sticky', !!(shown && sticky));
    const stickyH = sticky && !shown ? sticky.offsetHeight : 0;
    b.style.setProperty('--cc-bar-bottom', '0px');
    b.classList.remove('is-stacked');
    document.body.style.paddingBottom = shown ? `${b.offsetHeight}px` : '';
    html.style.setProperty('--ca-cart-bar-h', shown ? `${b.offsetHeight}px` : '0px');
    const covered = (shown ? b.offsetHeight : 0) + stickyH;
    html.style.setProperty('--ca-fixed-bottom', `${covered}px`);
    // the Smile rewards launcher steps aside while one of our bars has the bottom of the screen
    html.classList.toggle('ca-bottom-bar', covered > 0);
  };

  let barScrollY = 0;
  let barArm;
  const settleBar = () => {
    clearTimeout(barTimer);
    clearTimeout(barArm);
    window.removeEventListener('scroll', onBarScroll);
    if (barPhase !== 'added') return;
    barBefore = null;
    barInfo = null;
    renderBar(cfg.barPersist ? 'settled' : 'off');
  };
  // "it will disappear as you start to scroll again": a real scroll (not the jiggle of the tap) settles the bar early
  function onBarScroll() {
    if (Math.abs(window.scrollY - barScrollY) > 80) settleBar();
  }
  const showBar = (added, before) => {
    clearTimeout(barTimer);
    clearTimeout(barArm);
    window.removeEventListener('scroll', onBarScroll);
    barBefore = before;
    const first = added[0];
    const line = first && data?.lines?.[first.key];
    barInfo = line ? { title: line.title, meta: line.meta, image: line.image } : null;
    renderBar('added', barInfo);
    barArm = setTimeout(() => {
      barScrollY = window.scrollY;
      window.addEventListener('scroll', onBarScroll, { passive: true });
    }, 1000);
    barTimer = setTimeout(settleBar, (cfg.barSeconds || 4) * 1000);
  };

  // tablet / computer card under the basket icon
  let popTimer;
  const pop = () => $('[data-ca-cart-pop]');
  const placePop = () => {
    const p = pop();
    if (!p) return;
    const bags = Array.from(document.querySelectorAll('.ca-header__bag')).filter((el) => el.getClientRects().length);
    const bag = bags[0];
    let top = 16;
    let right = 16;
    if (bag) {
      const r = bag.getBoundingClientRect();
      if (r.bottom > 0) top = Math.round(r.bottom + 14);
      right = Math.max(8, Math.round(window.innerWidth - r.right - 8));
    }
    p.style.setProperty('--cc-pop-top', `${top}px`);
    p.style.setProperty('--cc-pop-right', `${right}px`);
  };
  const hidePop = () => {
    const p = pop();
    if (!p || p.hidden) return;
    clearTimeout(popTimer);
    p.classList.remove('is-shown');
    p.setAttribute('aria-hidden', 'true');
    const hadFocus = p.contains(document.activeElement);
    setTimeout(() => { if (!p.classList.contains('is-shown')) p.hidden = true; }, 220);
    if (hadFocus && lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
  };
  const armPopTimer = () => {
    clearTimeout(popTimer);
    popTimer = setTimeout(() => {
      const p = pop();
      if (p && (p.matches(':hover') || p.contains(document.activeElement))) return armPopTimer();
      hidePop();
    }, (cfg.popSeconds || 8) * 1000);
  };
  const showPop = (added, before) => {
    const p = pop();
    if (!p || !data) return;
    const I = iconTpl();
    const first = added[0];
    const line = (first && data.lines?.[first.key]) || null;
    const st = data.state;
    const crossedMin = !!before && before.meals < cfg.min && data.meals >= cfg.min;
    const toMin = cfg.min - data.meals;
    const toFree = cfg.free - data.meals;
    let status;
    if (st === 'voucher') {
      status = T.voucher ? `<div class="ca-cart-fb__line">${I['circle-check']}<span class="ca-cart-fb__head">${esc(T.voucher)}</span></div>` : '';
    } else if (st === 'min') {
      status = `<div class="ca-cart-fb__line ca-cart-fb__line--lock">${I.lock}<span class="ca-cart-fb__lock">${fill(esc(T.fbMin), { amount: strongAmt(toMin), min: esc(cfg.minLabel) })}</span></div>`;
    } else if (st === 'fee') {
      status = `<div class="ca-cart-fb__line">${I['circle-check']}<span class="ca-cart-fb__head">${fill(esc(T.fbReached), { min: esc(cfg.minLabel) })}</span></div><div class="ca-cart-fb__sub">${fill(esc(T.fbFeeMoreDesktop), { amount: strongAmt(toFree) })}</div>`;
    } else {
      status = `<div class="ca-cart-fb__line">${I['circle-check']}<span class="ca-cart-fb__head">${esc(T.fbFree)}</span></div><div class="ca-cart-fb__sub">${fill(esc(T.fbFreeSubDesktop), { min: esc(cfg.minLabel) })}</div>`;
    }
    void crossedMin;
    const qty = first?.qty || 1;
    const meta = line ? [line.meta, `Qty ${qty}`].filter(Boolean).join(' · ') : `Qty ${qty}`;
    const price = line ? money(line.price * qty) : '';
    p.innerHTML = `<div class="ca-cart-pop__head">${I['circle-check22']}<span class="ca-cart-pop__title" id="CaCartPopTitle">${esc(T.added)}</span><button type="button" class="ca-cart-pop__x" data-ca-pop-close aria-label="Close">${I.x}</button></div>
      <div class="ca-cart-pop__body">
        ${line ? `<div class="ca-cart-pop__item"><span class="ca-cart-fb__thumb">${line.image ? `<img src="${esc(line.image)}" alt="" width="64" height="64">` : ''}</span><span class="ca-cart-fb__text"><span class="ca-cart-fb__title">${esc(line.title)}</span><span class="ca-cart-fb__meta">${esc(meta)}</span></span><span class="ca-cart-pop__price">${esc(price)}</span></div><div class="ca-cart-pop__div"></div>` : ''}
        ${status ? `<div class="ca-cart-fb ca-cart-fb__status">${status}</div>` : ''}
        ${st === 'voucher' ? '' : progHTML(st, data.meals)}
        <div class="ca-cart-pop__btns"><button type="button" class="ca-cart-pop__view" data-ca-cart-open-pop aria-haspopup="dialog">${esc(T.view)} (${data.count})</button><button type="button" class="ca-cart-pop__cont" data-ca-pop-close>${esc(T.continue)}</button></div>
      </div>`;
    placePop();
    p.hidden = false;
    p.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => p.classList.add('is-shown'));
    armPopTimer();
  };

  const announce = (added) => {
    const live = $('[data-ca-cart-live]');
    const line = added[0] && data?.lines?.[added[0].key];
    if (live) live.textContent = `${T.added}${line ? `: ${line.title}` : ''}. ${itemsLabel(data.count)}, ${money(data.meals)}.`;
  };

  const feedback = (added, before) => {
    if (!data) return;
    lastFocus = document.activeElement;
    announce(added);
    if (isPhone()) showBar(added, before);
    else showPop(added, before);
  };

  /* ---------- checkout ---------- */
  const checkout = async (form, btn) => {
    btn?.setAttribute('aria-busy', 'true');
    footMessage('');
    try {
      const note = $('[data-ca-cart-note]');
      if (note) await saveNote(note);
      const cart = await D().syncServerTime(true);
      timeReady = true;
      if (!cart) throw new Error(T.error);
      lastCart = cart;
      const meals = cart.total_price - satLines(cart).reduce((n, i) => n + i.final_line_price, 0);
      if (mealsCount(cart) <= 0) { await refresh({ cart }); return; }
      if (voucherOnly(cart)) {
        // nothing to deliver: no minimum and no delivery day. Drop a Saturday charge or a day left over from removed dishes.
        const a = cart.attributes || {};
        const body = {};
        if (['Shipping-Date', 'preferred_day', 'Delivery Date'].some((k) => a[k])) body.attributes = { 'Shipping-Date': '', preferred_day: '', 'Delivery Date': '' };
        if (satLines(cart).length) body.updates = Object.fromEntries(satLines(cart).map((l) => [l.key, 0]));
        if (body.attributes || body.updates) await post('/cart/update.js', body);
        if (body.updates) { await refresh(); return; } // the total changed: show it before leaving
        window.location.href = '/checkout';
        return;
      }
      if (meals < cfg.min) { await refresh({ cart }); return; }
      const chosen = selectedDate || (cart.attributes || {})['Shipping-Date'];
      if (!D().isValidChoice(chosen)) {
        // the cut-off passed (or the day was closed) while the basket was open: move to the earliest day and say so
        const next = D().iso(D().earliest());
        await setDate(next);
        const help = $('[data-ca-day-help]');
        const msg = fill(T.dateMoved, { date: D().format(next, 'long') });
        if (help) { help.textContent = msg; help.hidden = false; help.classList.add('is-alert'); }
        footMessage(msg);
        $('[data-ca-cart-day]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      await reconcile(cart);
      const check = await getCart();
      const want = attrsFor(chosen);
      if (!check || Object.keys(want).some((k) => (check.attributes || {})[k] !== want[k])) await post('/cart/update.js', { attributes: want });
      window.location.href = '/checkout';
    } catch (err) {
      footMessage(err.message || T.error);
    } finally {
      btn?.removeAttribute('aria-busy');
    }
  };

  /* ---------- events ---------- */
  const onClick = async (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;

    // opening the basket from anywhere (header bag, legacy cart toggles, feedback bar/card)
    const opener = t.closest('.ca-header__bag, [data-ca-cart-open], .js-cart-toggle, .open-cart-btn, [data-ca-cart-open-bar], [data-ca-cart-open-pop]');
    if (opener && root && !(mode === 'page' && opener.matches('.ca-header__bag'))) {
      e.preventDefault();
      if (opener.matches('[data-ca-cart-open-pop]')) hidePop();
      open(opener.matches('[data-ca-cart-open-bar], [data-ca-cart-open-pop]') ? document.querySelector('.ca-header__bag') : opener);
      return;
    }
    if (opener && mode === 'page') { e.preventDefault(); open(); return; }
    // tapping the summary bar anywhere opens the basket too
    if (t.closest('[data-ca-cart-bar]')) { e.preventDefault(); open(document.querySelector('.ca-header__bag')); return; }
    if (t.closest('[data-ca-pop-close]')) { e.preventDefault(); hidePop(); return; }

    // checkout attempts outside the basket → show the basket instead (it is the one gate for the £39 minimum and the delivery day)
    const outsideCheckout = t.closest('a[href$="/checkout"], a[href*="/checkout?"], button[name="checkout"], input[name="checkout"]');
    if (outsideCheckout && root && !root.contains(outsideCheckout)) {
      e.preventDefault();
      open(outsideCheckout);
      return;
    }

    if (!root || !root.contains(t)) return;

    if (t.closest('[data-ca-cart-close]')) { e.preventDefault(); close(); return; }
    if (t.closest('[data-ca-sheet-close]')) { e.preventDefault(); closeSheet(); return; }

    const qtyBtn = t.closest('[data-ca-cart-qty]');
    if (qtyBtn) {
      e.preventDefault();
      const line = qtyBtn.closest('[data-ca-line]');
      if (!line) return;
      lineError(line.dataset.key, '');
      queueQty(line, Number(qtyBtn.dataset.caCartQty));
      return;
    }
    const rm = t.closest('[data-ca-cart-remove]');
    if (rm) {
      e.preventDefault();
      const line = rm.closest('[data-ca-line]');
      if (line && line.getAttribute('aria-busy') !== 'true') changeLine(line, 0);
      return;
    }
    if (t.closest('[data-ca-cart-clear]')) {
      e.preventDefault();
      try {
        await post('/cart/clear.js', {});
      } catch (err) { footMessage(err.message); }
      await refresh();
      return;
    }
    const addBtn = t.closest('[data-ca-cart-add]');
    if (addBtn) {
      e.preventDefault();
      if (addBtn.getAttribute('aria-busy') === 'true') return;
      addBtn.setAttribute('aria-busy', 'true');
      try {
        await add([{ id: addBtn.dataset.caCartAdd, quantity: 1 }], { source: 'basket', feedback: false });
      } catch (err) {
        footMessage(err.message);
      } finally {
        addBtn.removeAttribute('aria-busy');
      }
      return;
    }
    const sheetBtn = t.closest('[data-ca-sheet-open]');
    if (sheetBtn) { e.preventDefault(); openSheet(sheetBtn); return; }
    if (t.closest('[data-ca-sheet-more]')) {
      e.preventDefault();
      const s = sheet();
      $('[data-ca-sheet-list]', s).hidden = true;
      $('[data-ca-sheet-more]', s).hidden = true;
      const cal = $('[data-ca-sheet-cal]', s);
      cal.hidden = false;
      renderCal();
      cal.querySelector('.ca-cart-cal__day.is-on, .ca-cart-cal__day:not(:disabled)')?.focus({ preventScroll: true });
      return;
    }
    const nav = t.closest('[data-ca-cal-nav]');
    if (nav) {
      e.preventDefault();
      calMonth = new Date(Date.UTC(calMonth.getUTCFullYear(), calMonth.getUTCMonth() + Number(nav.dataset.caCalNav), 1));
      renderCal();
      $(`[data-ca-cal-nav="${nav.dataset.caCalNav}"]`, sheet())?.focus({ preventScroll: true });
      return;
    }
    const calDay = t.closest('[data-ca-cal-day]');
    if (calDay && !calDay.disabled) {
      e.preventDefault();
      pending = calDay.dataset.caCalDay;
      renderCal();
      $('[data-ca-sheet-confirm]', sheet()).textContent = fill(T.confirm, { date: D().format(pending, 'long') });
      $(`[data-ca-cal-day="${pending}"]`, sheet())?.focus({ preventScroll: true });
      return;
    }
    if (t.closest('[data-ca-sheet-confirm]')) {
      e.preventDefault();
      const iso = pending;
      closeSheet();
      const help = $('[data-ca-day-help]');
      help?.classList.remove('is-alert');
      footMessage('');
      if (iso && iso !== selectedDate) await setDate(iso);
      else renderDay();
      return;
    }
    const info = t.closest('[data-ca-note-info]');
    if (info) {
      e.preventDefault();
      const tip = document.getElementById(info.getAttribute('aria-controls'));
      if (tip) { tip.hidden = !tip.hidden; info.setAttribute('aria-expanded', String(!tip.hidden)); }
      return;
    }
    if (t.closest('[data-ca-save-hide]')) {
      e.preventDefault();
      saveHidden = true;
      try { sessionStorage.setItem('ca-cart-save-hidden', '1'); } catch (err) { /* private mode */ }
      paintSave();
      $('[data-ca-cart-checkout]')?.focus({ preventScroll: true });
      return;
    }
    const more = t.closest('[data-ca-cart-more]');
    if (more) {
      // already on the menu: just close the basket instead of reloading the page
      let here = false;
      try { here = new URL(more.href, location.href).pathname.replace(/\/$/, '') === location.pathname.replace(/\/$/, ''); } catch (err) { /* ignore */ }
      if (here && mode !== 'page') e.preventDefault();
      close();
    }
  };

  const onChange = (e) => {
    const t = e.target;
    if (!root || !root.contains(t)) return;
    if (t.matches('[data-ca-cart-sheet] input[type="radio"]')) {
      pending = t.value;
      $$('.ca-cart-opt', sheet()).forEach((o) => o.classList.toggle('is-on', o.contains(t)));
      $('[data-ca-sheet-confirm]', sheet()).textContent = fill(T.confirm, { date: D().format(pending, 'long') });
    }
    if (t.matches('[data-ca-cart-note]')) saveNote(t);
  };

  const onInput = (e) => {
    const t = e.target;
    if (!root || !t.matches || !t.matches('[data-ca-cart-note]')) return;
    if (t.value.length > cfg.noteMax) t.value = t.value.slice(0, cfg.noteMax);
    updateNoteCount(t);
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => saveNote(t), 700);
  };

  const onSubmit = (e) => {
    const form = e.target;
    if (!root) return;
    // any other form heading to checkout (e.g. a leftover theme or app cart form) → basket instead
    if (!root.contains(form)) {
      const action = (form.getAttribute('action') || '').toLowerCase();
      const viaCheckout = e.submitter && e.submitter.getAttribute('name') === 'checkout';
      if (/\/checkout(\?|$)/.test(action) || (viaCheckout && /\/cart(\?|$)/.test(action))) { e.preventDefault(); open(e.submitter || form); }
      return;
    }
    if (!form.matches('[data-ca-cart-checkout-form]')) return;
    e.preventDefault();
    checkout(form, form.querySelector('[data-ca-cart-checkout]'));
  };

  const onKey = (e) => {
    if (e.key === 'Escape') {
      if (sheetOpen()) { e.preventDefault(); closeSheet(); return; }
      if (isOpen()) { e.preventDefault(); close(); return; }
      if (pop() && !pop().hidden) { hidePop(); return; }
      return;
    }
    if (e.key === 'Enter' && e.target.matches?.('[data-ca-cart-note]')) {
      e.preventDefault();
      saveNote(e.target);
      return;
    }
    if (e.key !== 'Tab') return;
    let scope = null;
    if (sheetOpen()) scope = $('.ca-cart-sheet__panel', sheet());
    else if (isOpen()) scope = $('[data-ca-cart-panel]');
    if (!scope) return;
    const f = focusables(scope);
    if (!f.length) return;
    const first = f[0];
    const last = f[f.length - 1];
    if (!scope.contains(document.activeElement)) { e.preventDefault(); first.focus(); return; }
    if (e.shiftKey && (document.activeElement === first || document.activeElement === scope)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  // changes made by other scripts (old theme sections, apps): re-render
  let extTimer;
  const scheduleRefresh = () => {
    clearTimeout(extTimer);
    extTimer = setTimeout(() => refresh(), 250);
  };
  if (window.fetch && !window.fetch.__caCart) {
    const native = window.fetch;
    const wrapped = function (input, init) {
      const p = native.apply(this, arguments);
      try {
        const url = typeof input === 'string' ? input : input && input.url;
        const h = (init && init.headers) || {};
        const own = h['X-CA-Cart'] || h['X-CA-Menu-Card'] || (typeof h.get === 'function' && (h.get('X-CA-Cart') || h.get('X-CA-Menu-Card'))); // menu cards announce their own changes
        if (!own && url && /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url)) p.then((r) => { if (r && r.ok) scheduleRefresh(); }, () => {});
      } catch (err) { /* never break the caller */ }
      return p;
    };
    wrapped.__caCart = true;
    Object.keys(native).forEach((k) => { try { wrapped[k] = native[k]; } catch (e) { /* ignore */ } });
    window.fetch = wrapped;
  }

  /* ---------- init ---------- */
  const bind = () => {
    root = document.querySelector('[data-ca-cart-root]');
    if (!root) return false;
    try { cfg = JSON.parse(root.querySelector('[data-ca-cart-config]').textContent); } catch (e) { cfg = {}; }
    T = cfg.t || {};
    sectionId = root.dataset.sectionId;
    mode = root.dataset.mode;
    iconsCache = null;
    readData();
    updateBubble();
    return true;
  };

  const start = async () => {
    if (!bind()) return;
    paintSave();
    renderDay();
    if (!D()) return;
    const cart = await D().syncServerTime();
    timeReady = true;
    if (cart) {
      lastCart = cart;
      selectedDate = (cart.attributes || {})['Shipping-Date'] || selectedDate;
      // the page may have been cached with an older basket: re-render if it differs
      if (data && (cart.item_count !== data.itemCount || cart.total_price !== data.total)) await refresh({ cart });
      else await reconcile(cart);
    }
    renderDay();
    renderBar('settled', null, true);
  };

  document.addEventListener('click', onClick);
  document.addEventListener('change', onChange);
  document.addEventListener('input', onInput);
  document.addEventListener('submit', onSubmit, true);
  document.addEventListener('keydown', onKey);
  document.addEventListener('cart:updated', (e) => { if (e.detail && !e.detail.__ca) scheduleRefresh(); });
  document.addEventListener('ca:delivery-time', () => { renderDay(); if (sheetOpen()) renderSheet(); });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') syncIfStale(); });
  window.addEventListener('pageshow', (e) => { if (e.persisted) syncIfStale(); });
  window.addEventListener('resize', () => { if (pop() && !pop().hidden) placePop(); layoutBar(); }, { passive: true });

  // the product page sticky bar comes and goes as the page scrolls
  const watchSticky = () => {
    const s = document.querySelector('[data-ca-sticky]');
    if (!s || s.__caWatched) return;
    s.__caWatched = true;
    new MutationObserver(() => { renderBar('settled', null, true); layoutBar(); }).observe(s, { attributes: true, attributeFilter: ['class'] });
  };

  // theme editor
  document.addEventListener('shopify:section:load', (e) => { if (e.target.querySelector('[data-ca-cart-root]')) { bind(); renderDay(); } watchSticky(); });
  document.addEventListener('shopify:section:select', (e) => { if (e.target.querySelector('[data-ca-cart-root]')) open(); });
  document.addEventListener('shopify:section:deselect', (e) => { if (e.target.querySelector('[data-ca-cart-root]')) close(); });
  document.addEventListener('shopify:block:select', (e) => { if (root && root.contains(e.target)) open(); });

  /* ---------- public API + backwards compatibility ---------- */
  window.CACart = {
    __ready: true,
    add,
    refresh: (opts) => refresh(opts || {}),
    open,
    close,
    setDate,
    get data() { return data; },
    get selectedDate() { return selectedDate; },
  };
  window.openCartDrawer = (el) => open(el);
  if (!customElements.get('cart-slide')) {
    customElements.define('cart-slide', class extends HTMLElement {
      refreshCart() { return refresh(); }
      open() { open(); }
      close() { close(); }
    });
  }

  // Theme previews only: Shopify's preview bar is fixed over the bottom of the screen. Lift the basket's
  // checkout area and the "Added" bar above it so the preview behaves like the live site.
  const watchPreviewBar = () => {
    const html = document.documentElement;
    let wrap = null;
    const measure = () => {
      if (!wrap || !wrap.isConnected) {
        const frame = document.querySelector('iframe[src*="/preview-bar/"]');
        wrap = frame ? (frame.closest('body > *') || frame) : null;
      }
      const r = wrap && wrap.getClientRects().length ? wrap.getBoundingClientRect() : null;
      const h = r && r.height > 0 && r.bottom >= window.innerHeight - 2 ? Math.round(r.height) : 0;
      html.style.setProperty('--ca-pbar-h', `${h}px`);
    };
    measure();
    let tries = 0;
    const poll = setInterval(() => { measure(); if (++tries > 20) clearInterval(poll); }, 500); // the bar loads after the page
    window.addEventListener('resize', measure, { passive: true });
    document.addEventListener('click', () => setTimeout(measure, 400), true); // shown/hidden from its own buttons
  };

  const boot = () => {
    if (window.Shopify && Shopify.theme && Shopify.theme.role && Shopify.theme.role !== 'main') watchPreviewBar(); // never runs on the live theme
    if (!document.querySelector('cart-slide')) {
      const shim = document.createElement('cart-slide');
      shim.hidden = true;
      document.body.appendChild(shim);
    }
    watchSticky();
    start();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
